import i18n from '../i18n';
import { formatMoney } from '../i18n/format';
import { AddOn } from '../features/addOns/addOnsSlice';
import { Appointment } from '../features/serviceLocation/serviceLocationSlice';
import { ServiceType } from '../features/serviceTypes/serviceTypesSlice';
import {
  additionalFeeNames,
  discountedFeesNames,
} from '../constants/discountedFees';
import {
  isServiceNameKey,
  isSubscribeTo,
  isSubscriptionPlan,
  ServiceNameKeys,
} from './serviceKeys';
import { ServiceType as ServiceTypeName } from '../interfaces/WorkOrder';

/**
 * Client-side mirror of the server's booking price math. Every function here
 * works in whole cents and rounds the same way the server does, so the price a
 * customer sees is the price the server books (the server re-checks and
 * rejects the booking with SLOT_PRICE_CHANGED if they ever disagree).
 */

export const PaymentPreference = {
  payLater: 'PAY_LATER',
  prepay: 'PREPAY',
} as const;

export type PaymentPreferenceValue =
  typeof PaymentPreference[keyof typeof PaymentPreference];

export const SlotPriceTier = {
  discount: 'DISCOUNT',
  standard: 'STANDARD',
  peak: 'PEAK',
} as const;

export const DEFAULT_PREPAY_DISCOUNT_PERCENT = 3;

type PricedItem = Pick<
ServiceType | AddOn,
'value' | 'discountedValue' | 'nameKey'
> & { serviceTypeName?: string };

const effectiveValue = (item: PricedItem): number => (
  item.discountedValue !== null
  && item.discountedValue !== undefined
  && Number.isFinite(item.discountedValue)
    ? item.discountedValue
    : item.value
);

const toCents = (dollars: number): number => Math.round(dollars * 100);

const isFee = (item: PricedItem): boolean => (
  isServiceNameKey(item.nameKey) && additionalFeeNames.includes(item.nameKey)
);

/**
 * Subscription plans (and their onboarding fee) are a fixed price whatever
 * day they are booked, so slot pricing never touches them.
 */
export const isSubscriptionItem = (item: PricedItem): boolean => (
  item.serviceTypeName === ServiceTypeName.subscription
  || isSubscribeTo(item.nameKey)
  || isSubscriptionPlan(item.nameKey)
  || item.nameKey === ServiceNameKeys.subscriptionPlanAnnualAllInclusive
);

/**
 * Whether a service can carry an airline-style slot price on a regular
 * (non-plan) booking.
 */
export const isSlotPriceEligible = (item: PricedItem): boolean => (
  !isFee(item) && !isSubscriptionItem(item)
);

export const isMobileServiceFee = (item: PricedItem): boolean => (
  item.nameKey === ServiceNameKeys.mobileServiceFee
  || item.nameKey === ServiceNameKeys.mobileServiceFeeSubscription
  || item.nameKey === ServiceNameKeys.mobileServiceFeeAllInclusiveSubscription
  || item.nameKey === ServiceNameKeys.mobileServiceFeeAMZ
  || item.nameKey === ServiceNameKeys.mobileServiceFeeAMZMainService
);

/**
 * On a plan booking the plan price is fixed, so the slot adjustment applies
 * to the mobile service fee and the one-time onboarding service instead.
 * Mirrors the server's PLAN_BOOKING_SLOT_PRICED_NAME_KEYS.
 */
export const isPlanBookingSlotPriced = (item: PricedItem): boolean => (
  isMobileServiceFee(item) || isSubscribeTo(item.nameKey)
);

/** True when the basket contains a subscription plan or its onboarding. */
export const isPlanBooking = (items: PricedItem[]): boolean => (
  items.some(isSubscriptionItem)
);

/**
 * Subtotal (in cents) of the services and add-ons a slot price percentage
 * applies to: everything the customer picked except fees.
 */
export function eligibleSubtotalCents(items: PricedItem[]): number {
  const eligible = isPlanBooking(items)
    ? items.filter(isPlanBookingSlotPriced)
    : items.filter(isSlotPriceEligible);
  return eligible.reduce((sum, item) => sum + toCents(effectiveValue(item)), 0);
}

/**
 * "From" price (dollars) for a single service given the best percentage a
 * customer can reach by picking a good time (e.g. -15). Returns null when
 * no discount exists or the service is not slot-priced.
 */
export function fromPriceDollars(
  item: PricedItem,
  bestAdjustmentPercent: number,
): number | null {
  if (bestAdjustmentPercent >= 0 || !isSlotPriceEligible(item)) return null;
  const cents = toCents(effectiveValue(item));
  if (cents <= 0) return null;
  return (cents + Math.round((cents * bestAdjustmentPercent) / 100)) / 100;
}

/**
 * The basket as it will be on the day of a plan booking's first visit.
 *
 * The mobile service fee is only added to the basket once a time is picked
 * (routes/Schedule.tsx), so until then a plan basket is plan + onboarding
 * alone and a price read off it is $20 short of the plan card's "due at your
 * first visit". This adds the next visit's fee as a stand-in line when a plan
 * basket does not carry one yet, so every figure shown for one basket is the
 * same. Non-plan baskets, and plan baskets that already carry the fee, are
 * returned unchanged.
 */
export function withPlanVisitFee<T extends PricedItem>(
  items: T[],
  nextVisitFeeCents: number | null,
): PricedItem[] {
  if (!isPlanBooking(items)) return items;
  if (items.some(isMobileServiceFee)) return items;
  if (!nextVisitFeeCents || nextVisitFeeCents <= 0) return items;
  return [
    ...items,
    {
      value: nextVisitFeeCents / 100,
      discountedValue: undefined,
      nameKey: ServiceNameKeys.mobileServiceFeeSubscription,
    },
  ];
}

/**
 * Pre-tax subtotal (in cents) of everything on the order, fees included.
 */
export function orderSubtotalCents(items: PricedItem[]): number {
  return items.reduce((sum, item) => sum + toCents(effectiveValue(item)), 0);
}

/** A code that sets the plan join fee to $0 (WELCOMEBACK). */
export const JOIN_FEE_WAIVER = 'JOIN_FEE_WAIVER';

/**
 * Cents an applied discount code takes off the basket, as the order summary
 * (components/Invoice.tsx) takes it: the stored amount is basis points for a
 * PERCENT code, dollars otherwise; a Groupon-style code also takes the
 * discounted fees. The booking's own amount is the server's.
 */
export function codeDiscountCents(
  discountType: string | null | undefined,
  storedAmount: number | null | undefined,
  items: PricedItem[],
): number {
  if (!storedAmount) return 0;
  if (discountType === JOIN_FEE_WAIVER) {
    // The join fees on the order (every car starting a plan), never more
    // than the server validated (server join-fee-waiver.ts).
    const fees = orderSubtotalCents(items.filter(
      (item) => isSubscribeTo(item.nameKey),
    ));
    return Math.min(fees, toCents(storedAmount));
  }
  if (discountType === 'PERCENT') {
    return Math.round((orderSubtotalCents(items) * storedAmount) / 10000);
  }
  const cents = toCents(storedAmount);
  if (discountType === 'PROMO') return cents;
  return cents + orderSubtotalCents(items.filter(
    (item) => isServiceNameKey(item.nameKey)
      && discountedFeesNames.includes(item.nameKey),
  ));
}

/**
 * Signed slot adjustment (in cents) for a slot and an eligible subtotal.
 * Negative = discount.
 */
export function slotAdjustmentCents(
  slot: Pick<Appointment, 'priceAdjustmentPercent' | 'priceAdjustmentCents'>
  | null | undefined,
  eligibleCents: number,
): number {
  if (!slot) return 0;
  const percent = slot.priceAdjustmentPercent ?? 0;
  const fixed = slot.priceAdjustmentCents ?? 0;
  return Math.round((eligibleCents * percent) / 100) + Math.round(fixed);
}

/**
 * Prepay discount (in cents, negative) for a pre-tax subtotal.
 */
export function prepayDiscountCents(
  subtotalCents: number,
  percent: number = DEFAULT_PREPAY_DISCOUNT_PERCENT,
): number {
  if (subtotalCents <= 0 || percent <= 0) return 0;
  return -Math.round((subtotalCents * percent) / 100);
}

/** "$12.34" ("12,34 $" in French: i18n/format.ts). */
export function formatCents(cents: number): string {
  return formatMoney(Math.abs(cents) / 100);
}

/** "+$12.34" / "-$12.34" / "$0.00" */
export function formatSignedCents(cents: number): string {
  if (cents === 0) return formatCents(0);
  return `${cents > 0 ? '+' : '-'}${formatCents(cents)}`;
}

/** Short customer-facing label for a slot's price tier. */
export function slotTierLabel(
  slot: Pick<Appointment, 'priceTier' | 'isLowestPrice'> | null | undefined,
): string | null {
  if (!slot) return null;
  if (slot.isLowestPrice) return i18n.t('schedule.tier.lowest');
  if (slot.priceTier === SlotPriceTier.peak) return i18n.t('schedule.tier.peak');
  if (slot.priceTier === SlotPriceTier.discount) {
    return i18n.t('schedule.tier.reduced');
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * Aperio Mobility plan pricing
 *
 * Client mirror of server/src/modules/pricing-formula/aperio-plan-pricing.ts,
 * which is itself the published program in aperio/site (PRICING-PROGRAM.md,
 * src/data/pricing.js, build/util.js). Since 2026-10-24 every surcharge
 * figure is a tenant setting (AdminJS Tenant Settings); these are the
 * defaults, and the server sends the tenant's own figures with each plan
 * price (`planPriceBreakdown`):
 *
 *   annual plan = base $199
 *               + $10 a year per litre of oil over 5 L, pro rata
 *               + $80 a year for a European make
 *               + $150 a year for a diesel engine, on top of the litres
 *                 (a European diesel pays the larger, once; neither on an
 *                 electric vehicle)
 *               the surcharges together rounded to the nearest $5
 *   visit fee   = $20 for the first 4 visits per vehicle per plan year,
 *                 $30 per visit after that, and nothing on the visit that
 *                 starts a plan: its one-time onboarding includes it
 *
 * Only tenants whose pricing formula is `aperio-plan` use it; the server
 * sends the finished numbers, and these functions keep the booking UI able to
 * work them out for itself so displayed can never drift from charged.
 * ------------------------------------------------------------------ */

/** The plan surcharge figures (server AperioPlanSurchargeSettings). */
export interface AperioPlanSurcharges {
  /** Litres per oil change the base plan covers. */
  includedLitres: number;
  /** Cents a year per litre over the allowance, pro rata. */
  extraLitreCents: number;
  /** Cents a year for a European make. */
  europeanCents: number;
  /** Cents a year for a diesel engine (a European diesel: the larger). */
  dieselCents: number;
  /** The surcharges together are rounded to the nearest this, cents. */
  roundToCents: number;
}

/** The owner's figures of 2026-10-24 (the server defaults). */
export const APERIO_PLAN_SURCHARGE_DEFAULTS: Readonly<AperioPlanSurcharges> = {
  includedLitres: 5,
  extraLitreCents: 1000,
  europeanCents: 8000,
  dieselCents: 15000,
  roundToCents: 500,
};

/** Oil capacity, in litres, the base plan price covers by default. */
export const APERIO_INCLUDED_LITRES = APERIO_PLAN_SURCHARGE_DEFAULTS.includedLitres;

/** Default: $10 a year for each litre over the allowance, pro rata. */
export const APERIO_EXTRA_LITRE_CENTS = APERIO_PLAN_SURCHARGE_DEFAULTS.extraLitreCents;

/** $20 mobile service fee for the visits inside the allowance. */
export const APERIO_MOBILE_FEE_CENTS = 2000;

/** $30 mobile service fee once the allowance is used up. */
export const APERIO_MOBILE_FEE_HIGH_USE_CENTS = 3000;

/** Visits per vehicle per plan year charged at the standard fee. */
export const APERIO_MOBILE_FEE_VISITS_INCLUDED = 4;

/** European makes, as the Aperio site lists them (EURO_MAKES in util.js). */
export const APERIO_EURO_MAKES = [
  'alfa-romeo', 'audi', 'bmw', 'fiat', 'jaguar', 'land-rover', 'maserati',
  'mercedes-benz', 'mini', 'peugeot', 'porsche', 'saab', 'smart', 'sterling',
  'volkswagen', 'volvo',
];

// eslint-disable-next-line max-len
const APERIO_DIESEL_RE = /DIESEL|\bDSL\b|\bTDI\b|\bCDI\b|BLUETEC|DURAMAX|POWER ?STROKE|CUMMINS|ECODIESEL/i;

const slugifyMake = (make?: string | null): string => String(make ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/** True when the make is one of the European marques. */
export const isAperioEuroMake = (make?: string | null): boolean => (
  APERIO_EURO_MAKES.includes(slugifyMake(make))
);

/** True when the engine string names a diesel engine. */
export const isAperioDieselEngine = (engine?: string | null): boolean => (
  engine !== null
  && engine !== undefined
  && APERIO_DIESEL_RE.test(String(engine))
);

/**
 * Litres over the allowance, pro rata to the hundredth (6.5 L with 5 L
 * included is 1.5). Rounded to 2 dp because oil volumes are 1-dp decimals
 * and 7.3 - 6 is 1.2999999999999998 in binary floating point.
 */
export function aperioExtraLitres(
  oilVolumeLitres?: number | null,
  surcharges: AperioPlanSurcharges = APERIO_PLAN_SURCHARGE_DEFAULTS,
): number {
  const litres = Number(oilVolumeLitres);
  if (!Number.isFinite(litres) || litres <= 0) return 0;
  const over = Math.round((litres - surcharges.includedLitres) * 100) / 100;
  return over > 0 ? over : 0;
}

export interface AperioPlanPriceInput {
  make?: string | null;
  engine?: string | null;
  /**
   * The vehicle's powertrain as the server reports it (`ELECTRIC`, `GAS`,
   * ...). An ELECTRIC vehicle pays no European/diesel surcharge.
   */
  powertrain?: string | null;
  oilVolumeLitres?: number | null;
  /** The admin-set plan price in cents ($199 consumer, $250 fleet). */
  basePriceCents: number;
  /** The tenant's figures; the defaults when omitted. */
  surcharges?: AperioPlanSurcharges | null;
}

/** The annual plan price for one vehicle, in cents. */
export function aperioPlanPriceCents(input: AperioPlanPriceInput): number {
  const settings = input.surcharges ?? APERIO_PLAN_SURCHARGE_DEFAULTS;
  const base = Math.round(Number(input.basePriceCents) || 0);
  const electric = input.powertrain === 'ELECTRIC';
  const european = !electric && isAperioEuroMake(input.make)
    ? settings.europeanCents : 0;
  const diesel = !electric && isAperioDieselEngine(input.engine)
    ? settings.dieselCents : 0;
  const litres = Math.round(
    aperioExtraLitres(input.oilVolumeLitres, settings) * settings.extraLitreCents,
  );
  const raw = Math.max(european, diesel) + litres;
  const step = Math.round(settings.roundToCents);
  return base + (step > 1 ? Math.round(raw / step) * step : raw);
}

/**
 * The mobile service fee for the NEXT visit, given how many visits this
 * vehicle already had this plan year: visits 1-4 (0-3 previous) cost $20, the
 * 5th onwards (4+ previous) cost $30.
 */
export function aperioMobileFeeCents(
  visitsThisPlanYear: number,
  standardFeeCents: number = APERIO_MOBILE_FEE_CENTS,
  highUseFeeCents: number = APERIO_MOBILE_FEE_HIGH_USE_CENTS,
  visitsIncluded: number = APERIO_MOBILE_FEE_VISITS_INCLUDED,
): number {
  const previous = Number.isFinite(visitsThisPlanYear)
    ? Math.max(0, Math.floor(visitsThisPlanYear))
    : 0;
  return previous >= visitsIncluded
    ? Math.round(highUseFeeCents)
    : Math.round(standardFeeCents);
}

/** What the booking UI needs to explain the per-visit fee. */
export interface MobileServiceFeeConfig {
  mobileServiceFeeCents?: number | null;
  /**
   * The All-Inclusive plan's own fee row, for the visits inside the
   * allowance (Aperio: $0). Null when the tenant has no such row.
   */
  mobileServiceFeeAllInclusiveCents?: number | null;
  mobileServiceFeeHighUseCents?: number | null;
  mobileServiceFeeVisitsIncluded?: number | null;
  planYearVisitCount?: number | null;
  /**
   * True when the visit that starts a plan (the basket carries the plan's
   * one-time onboarding) is charged no mobile service fee: the onboarding
   * includes it (Aperio). False / absent for every other tenant.
   */
  planStartMobileFeeIncluded?: boolean | null;
}

/**
 * The fee the NEXT visit will be charged, in cents: the stored fee for a
 * tenant whose fee never escalates, or the Aperio high-use fee once this
 * vehicle has used its standard-fee visits for the plan year. Null when the
 * tenant has no fee on file.
 */
export function mobileServiceFeeNextVisitCents(
  config: MobileServiceFeeConfig,
): number | null {
  const fee = config.mobileServiceFeeCents;
  if (fee === null || fee === undefined) return null;
  const highUse = config.mobileServiceFeeHighUseCents;
  const included = config.mobileServiceFeeVisitsIncluded;
  if (!highUse || !included) return fee;
  return aperioMobileFeeCents(
    config.planYearVisitCount ?? 0,
    fee,
    highUse,
    included,
  );
}

/** True when the basket is an All-Inclusive plan booking. */
export const isAllInclusivePlanBooking = (items: PricedItem[]): boolean => (
  items.some((item) => (
    item.nameKey === ServiceNameKeys.subscribeToAllInclusiveAnnual
    || item.nameKey === ServiceNameKeys.subscriptionPlanAnnualAllInclusive
  ))
);

/**
 * The fee a plan's NEXT visit is charged, in cents. The All-Inclusive plan
 * has its own fee row (Aperio: $0 for the visits inside the allowance, the
 * high-use fee after), which the server charges instead of the standard fee
 * (PricingFormulaService.mobileServiceFeeCents); a tenant with no such row
 * adds no fee to an All-Inclusive visit at all, so that counts as $0 too.
 * Every other basket gets mobileServiceFeeNextVisitCents.
 */
export function planVisitFeeCents(
  config: MobileServiceFeeConfig,
  allInclusive: boolean,
): number | null {
  if (!allInclusive) return mobileServiceFeeNextVisitCents(config);
  return mobileServiceFeeNextVisitCents({
    ...config,
    mobileServiceFeeCents: config.mobileServiceFeeAllInclusiveCents ?? 0,
  });
}

/** True when the basket starts a plan: it carries a plan's onboarding. */
export const isPlanStartBasket = (
  items: Pick<PricedItem, 'nameKey'>[],
): boolean => items.some((item) => isSubscribeTo(item.nameKey));

/**
 * The fee the FIRST visit of a plan (the one that carries the onboarding) is
 * charged, in cents: nothing on a tenant whose onboarding includes it
 * (planStartMobileFeeIncluded, Aperio), the plan's next-visit fee otherwise
 * (planVisitFeeCents). Every figure quoted for a plan's first visit - the
 * plan cards' "Due at first visit", the time slots, the summary bar - reads
 * this, and the server charges the same (settlePlanStartMobileFee).
 */
export function firstPlanVisitFeeCents(
  config: MobileServiceFeeConfig,
  allInclusive: boolean,
): number | null {
  if (config.planStartMobileFeeIncluded) return 0;
  return planVisitFeeCents(config, allInclusive);
}

/**
 * True for a basket's mobile service fee line that the plan's onboarding
 * includes: listed as "Included with onboarding" rather than "$0.00" or not
 * at all.
 */
export function isFeeIncludedWithOnboarding(
  item: PricedItem,
  basket: Pick<PricedItem, 'nameKey'>[],
  config: Pick<MobileServiceFeeConfig, 'planStartMobileFeeIncluded'>,
): boolean {
  return !!config.planStartMobileFeeIncluded
    && isMobileServiceFee(item)
    && toCents(effectiveValue(item)) === 0
    && isPlanStartBasket(basket);
}

/**
 * "$20.00 mobile service fee per visit", plus the high-use clause for tenants
 * whose fee escalates. Null when the tenant has no fee on file.
 */
export function mobileServiceFeeHint(
  config: MobileServiceFeeConfig,
): string | null {
  const nextFee = mobileServiceFeeNextVisitCents(config);
  if (nextFee === null || nextFee <= 0) return null;
  const highUse = config.mobileServiceFeeHighUseCents;
  const included = config.mobileServiceFeeVisitsIncluded;
  if (!highUse || !included) {
    return i18n.t('product.feePerVisit', { price: formatCents(nextFee) });
  }
  return i18n.t('product.feePerVisitHighUse', {
    price: formatCents(nextFee),
    count: included,
    high: formatCents(highUse),
  });
}
